import React, { useState } from 'react';
import './AIGenerationInfo.css';
import { Bot, CheckCircle, Copy, Check, Sparkles, AlertTriangle, Info, ShieldCheck, Clock, Zap } from 'lucide-react';

function AIGenerationInfo({ metadata, fileType }) {
  const [copied, setCopied] = useState(false);

  const getAIInfo = () => {
    if (!metadata) return null;

    return {
      generator: metadata.generator || metadata.aiGenerator || null,
      prompt: metadata.prompt || metadata.aiPrompt || null,
      isAIGenerated: metadata.isAIGenerated || false,
      confidence: metadata.confidence || 0,
      aiType: metadata.aiType || 'unknown',
      enhancementTool: metadata.enhancementTool || null,
      evidence: metadata.evidence || [],
      deepScanResults: metadata.deepScanResults || [],
      c2paVerified: metadata.c2paVerified || false,
      iptcVerified: metadata.iptcVerified || false,
    };
  };

  const aiInfo = getAIInfo();

  const handleCopy = () => {
    if (aiInfo) {
      const text = aiInfo.prompt
        ? `Generator: ${aiInfo.generator || 'Unknown'}\nPrompt: ${aiInfo.prompt}`
        : `Generator: ${aiInfo.generator || 'None detected'}`;
      navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const getConfidenceInfo = (confidence) => {
    if (confidence >= 0.9) return { label: 'Very High', color: '#00ff88', bgColor: 'rgba(0, 255, 136, 0.15)' };
    if (confidence >= 0.7) return { label: 'High', color: '#00ccff', bgColor: 'rgba(0, 204, 255, 0.15)' };
    if (confidence >= 0.5) return { label: 'Medium', color: '#ffcc00', bgColor: 'rgba(255, 204, 0, 0.15)' };
    if (confidence > 0) return { label: 'Low', color: '#ff8844', bgColor: 'rgba(255, 136, 68, 0.15)' };
    return { label: 'Unknown', color: '#888', bgColor: 'rgba(136, 136, 136, 0.15)' };
  };

  const getDetectionMethod = () => {
    if (!aiInfo) return null;

    const methods = [];

    if (aiInfo.c2paVerified) {
      methods.push({ icon: ShieldCheck, label: 'C2PA Content Credentials', desc: 'Cryptographically signed by creator' });
    }
    if (aiInfo.iptcVerified) {
      methods.push({ icon: ShieldCheck, label: 'IPTC Digital Source', desc: 'Standard AI content label' });
    }
    if (aiInfo.prompt) {
      methods.push({ icon: Zap, label: 'Embedded Prompt', desc: 'AI generation prompt found in metadata' });
    }
    if (aiInfo.evidence && aiInfo.evidence.length > 0) {
      aiInfo.evidence.forEach(e => {
        if (e.includes('C2PA')) {
          methods.push({ icon: ShieldCheck, label: 'C2PA Marker', desc: e });
        } else if (e.includes('IPTC')) {
          methods.push({ icon: ShieldCheck, label: 'IPTC Metadata', desc: e });
        } else if (e.includes('frame_rate') || e.includes('fps')) {
          methods.push({ icon: Clock, label: '24fps Heuristic', desc: 'Frame rate typical of AI video' });
        } else if (e.includes('Filename')) {
          methods.push({ icon: Info, label: 'Filename Pattern', desc: 'UUID pattern in filename' });
        } else {
          methods.push({ icon: Info, label: 'Metadata Match', desc: e });
        }
      });
    }

    // If we have a generator but no specific methods, show generic
    if (methods.length === 0 && aiInfo.generator) {
      methods.push({ icon: Bot, label: 'Software Signature', desc: `Detected ${aiInfo.generator} signature in file` });
    }

    return methods;
  };

  const hasContent = aiInfo && (aiInfo.generator || aiInfo.prompt || aiInfo.isAIGenerated);
  const confidenceInfo = aiInfo ? getConfidenceInfo(aiInfo.confidence) : null;
  const detectionMethods = getDetectionMethod();

  return (
    <div className="ai-generation-info">
      <div className="panel-header">
        <h2><Sparkles size={16} /> AI Detection</h2>
        {hasContent && (
          <button className="copy-btn" onClick={handleCopy}>
            {copied ? <><Check size={14} /> Copied</> : <><Copy size={14} /> Copy</>}
          </button>
        )}
      </div>

      {fileType?.startsWith('image/') || fileType?.startsWith('video/') ? (
        hasContent ? (
          <div className="ai-content">
            {/* Detection Banner */}
            <div className="detection-banner" style={{ borderColor: confidenceInfo.color }}>
              <div className="detection-icon" style={{ color: confidenceInfo.color }}>
                <Bot size={24} />
              </div>
              <div className="detection-info">
                <span className="detection-label">AI Generated</span>
                <span className="detection-type">
                  {aiInfo.aiType === 'enhanced' ? 'AI Enhanced/Altered' : 'AI Created'}
                </span>
              </div>
              {(aiInfo.c2paVerified || aiInfo.iptcVerified) && (
                <span className="verified-badge">
                  <CheckCircle size={12} /> Verified
                </span>
              )}
            </div>

            {/* Confidence Meter */}
            {aiInfo.confidence > 0 && (
              <div className="confidence-section">
                <div className="confidence-header">
                  <span className="confidence-label">Confidence</span>
                  <span className="confidence-value" style={{ color: confidenceInfo.color }}>
                    {Math.round(aiInfo.confidence * 100)}%
                  </span>
                </div>
                <div className="confidence-bar-bg">
                  <div
                    className="confidence-bar-fill"
                    style={{
                      width: `${aiInfo.confidence * 100}%`,
                      backgroundColor: confidenceInfo.color
                    }}
                  />
                </div>
                <span className="confidence-text" style={{ color: confidenceInfo.color }}>
                  {confidenceInfo.label} Confidence
                </span>
              </div>
            )}

            {/* Generator */}
            {aiInfo.generator && (
              <div className="info-row">
                <span className="info-label">Generator</span>
                <span className="info-value generator-name">{aiInfo.generator}</span>
              </div>
            )}

            {/* Enhancement Tool */}
            {aiInfo.enhancementTool && (
              <div className="info-row">
                <span className="info-label">Tool Type</span>
                <span className="info-value tool-badge">{aiInfo.enhancementTool}</span>
              </div>
            )}

            {/* Detection Methods - WHY it was detected */}
            {detectionMethods && detectionMethods.length > 0 && (
              <div className="detection-reasons">
                <span className="section-label">
                  <Info size={14} /> Why was this detected?
                </span>
                <ul className="reasons-list">
                  {detectionMethods.map((method, i) => (
                    <li key={i}>
                      <method.icon size={14} className="reason-icon" />
                      <div className="reason-content">
                        <span className="reason-label">{method.label}</span>
                        <span className="reason-desc">{method.desc}</span>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Prompt */}
            {aiInfo.prompt && (
              <div className="prompt-box">
                <span className="section-label">Detected Prompt</span>
                <pre className="prompt-text">{aiInfo.prompt}</pre>
              </div>
            )}

            {/* Deep Scan Results */}
            {aiInfo.deepScanResults && aiInfo.deepScanResults.length > 1 && (
              <div className="deep-scan-box">
                <span className="section-label">
                  Additional Findings ({aiInfo.deepScanResults.length})
                </span>
                <ul className="deep-scan-list">
                  {aiInfo.deepScanResults.slice(1, 4).map((result, i) => (
                    <li key={i}>
                      {result.generator && <span className="gen-tag">{result.generator}</span>}
                      <span className="scan-text">
                        {result.text.length > 100 ? result.text.substring(0, 100) + '...' : result.text}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        ) : (
          <div className="no-detection">
            <div className="no-icon-container">
              <CheckCircle size={32} />
            </div>
            <p className="no-data">No AI Detected</p>
            <p className="no-subtext">This file doesn't contain recognizable AI generation signatures</p>
          </div>
        )
      ) : (
        <div className="no-detection">
          <div className="no-icon-container warning">
            <AlertTriangle size={32} />
          </div>
          <p className="no-data">Unsupported File</p>
          <p className="no-subtext">Please upload an image or video file</p>
        </div>
      )}
    </div>
  );
}

export default AIGenerationInfo;